import { ROUTES } from '@/lib/routes'
import type { Box, CertificateTemplate, LayoutField, LayoutKey } from '@/types/certificates'

/**
 * Date and time of issue, in the certificate's own time zone (decision 11): the
 * printed hour is the template's, not the reader's. The zone name goes along,
 * so "14:00" read from Lisbon is not mistaken for local time.
 */
export const formatIssuedAt = (iso: string | null | undefined, locale: string, timeZone?: string, withZone = true): string => {
  if (!iso) return ''
  const date = new Date(iso)
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: timeZone || undefined
    }).format(date) + (timeZone && withZone ? ` (${timeZone.replace(/_/g, ' ')})` : '')
  } catch {
    return date.toLocaleString(locale)
  }
}

/** Just the day, for cards and rows where the hour is noise. */
export const formatIssuedDay = (iso: string | null | undefined, locale: string, timeZone?: string): string => {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: timeZone || undefined }).format(new Date(iso))
  } catch {
    return new Date(iso).toLocaleDateString(locale)
  }
}

/** The languages a template can print in: the app's own. */
export const TEMPLATE_LANGUAGES = [
  'pt-BR', 'pt-PT', 'en-US', 'es-ES', 'es-MX', 'es-AR', 'it-IT', 'de-DE', 'de-AT', 'de-CH', 'fr-FR'
] as const

/** Every IANA zone the browser knows, or a short list where it cannot say. */
export const timeZones = (): string[] => {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  try {
    const all = intl.supportedValuesOf?.('timeZone')
    if (all && all.length) return all
  } catch {
    /* falls through */
  }
  return ['America/Sao_Paulo', 'America/Manaus', 'America/Mexico_City', 'America/Argentina/Buenos_Aires',
    'America/New_York', 'Europe/Lisbon', 'Europe/Madrid', 'Europe/Rome', 'Europe/Berlin', 'UTC']
}

export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo'
  } catch {
    return 'America/Sao_Paulo'
  }
}

/**
 * The variables a description accepts, filled in at issue. `{curso}` and
 * `{evento}` still work on the server as aliases of `{assunto}`, but are no
 * longer offered.
 */
export const DESCRIPTION_VARIABLES = ['{nome}', '{assunto}', '{cargaHoraria}'] as const

/**
 * Fills a description the way the server does, for the canvas sample: square
 * brackets mark an optional excerpt, dropped when a variable inside is empty.
 */
export const fillDescription = (text: string, values: Record<string, string>) => {
  const sub = (s: string) => s.replace(/\{(\w+)\}/g, (_, key) => values[key === 'curso' || key === 'evento' ? 'assunto' : key] ?? '')
  return text
    .replace(/\[([^\]]*)\]/g, (_, inner: string) => {
      const keys = Array.from(inner.matchAll(/\{(\w+)\}/g), (m) => m[1])
      return keys.every((k) => (values[k === 'curso' || k === 'evento' ? 'assunto' : k] ?? '').trim()) ? sub(inner) : ''
    })
    .replace(/\{(\w+)\}/g, (m) => sub(m))
}

/** Lowercase, numbers and hyphens: what the API accepts as a template slug. */
export const toSlug = (text: string) =>
  text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').slice(0, 60)

export const REQUIRED_FIELDS: LayoutKey[] = ['recipientName', 'issuedAt', 'qr']
export const OPTIONAL_FIELDS: LayoutKey[] = [
  'title', 'description', 'workload', 'subjectTitle', 'code', 'signer1', 'signer2', 'signer1Signature', 'signer2Signature'
]

/**
 * Boxes that print a signer's signature image instead of text: the index of
 * the signer each one belongs to. They only take a box and a horizontal
 * alignment; the image fills the box height and shrinks to fit its width.
 */
export const SIGNATURE_FIELDS: Partial<Record<LayoutKey, number>> = { signer1Signature: 0, signer2Signature: 1 }

export const isSignatureField = (key: LayoutKey) => SIGNATURE_FIELDS[key] !== undefined

const FULL_AREA: Box = { x: 0, y: 0, w: 100, h: 100 }

export const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/** Rounded to two decimals: the API stores what it gets, and 41.000000003 helps nobody. */
export const round2 = (value: number) => Math.round(value * 100) / 100

/** Keeps a box whole inside the area that reaches the PDF. */
export const fitBox = (box: Box, area: Box | null): Box => {
  const a = area || FULL_AREA
  const w = clamp(box.w, 1, a.w)
  const h = clamp(box.h, 1, a.h)
  return {
    x: round2(clamp(box.x, a.x, a.x + a.w - w)),
    y: round2(clamp(box.y, a.y, a.y + a.h - h)),
    w: round2(w),
    h: round2(h)
  }
}

/**
 * A new field's starting box and style.
 *
 * The QR is kept square on the artwork: its height in percent of the height is
 * its width scaled by the art's proportion, or a 10% box on a wide art comes
 * out as a flat rectangle.
 */
export const defaultField = (key: LayoutKey, template: Pick<CertificateTemplate, 'backgroundWidth' | 'backgroundHeight' | 'visibleArea'>): LayoutField => {
  const area = template.visibleArea || FULL_AREA
  const ratio = template.backgroundWidth && template.backgroundHeight
    ? template.backgroundWidth / template.backgroundHeight
    : Math.SQRT2

  if (key === 'qr') {
    const w = Math.min(10, area.w * 0.15)
    const h = w * ratio
    return {
      key,
      box: fitBox({ x: area.x + area.w - w - 4, y: area.y + area.h - h - 5, w, h }, area),
      color: '#111827'
    }
  }

  if (isSignatureField(key)) {
    /* Wide and low, right above the signer's name box. */
    return { key, box: fitBox({ x: key === 'signer1Signature' ? 12 : 58, y: 70, w: 30, h: 7 }, area), align: 'center' }
  }

  const presets: Partial<Record<LayoutKey, { y: number; h: number; size: number; minSize: number; weight: 400 | 700; font: string }>> = {
    title: { y: 18, h: 9, size: 5, minSize: 3, weight: 700, font: 'playfair-display' },
    recipientName: { y: 38, h: 12, size: 7, minSize: 3.5, weight: 700, font: 'playfair-display' },
    description: { y: 52, h: 10, size: 2.4, minSize: 1.6, weight: 400, font: 'inter' },
    issuedAt: { y: 64, h: 5, size: 2.2, minSize: 1.6, weight: 400, font: 'inter' },
    workload: { y: 70, h: 4, size: 2, minSize: 1.4, weight: 400, font: 'inter' },
    subjectTitle: { y: 46, h: 6, size: 3, minSize: 2, weight: 700, font: 'inter' },
    code: { y: 88, h: 4, size: 1.6, minSize: 1.2, weight: 400, font: 'inter' },
    signer1: { y: 78, h: 6, size: 2, minSize: 1.4, weight: 400, font: 'inter' },
    signer2: { y: 78, h: 6, size: 2, minSize: 1.4, weight: 400, font: 'inter' }
  }
  const preset = presets[key] || { y: 45, h: 6, size: 2.5, minSize: 1.6, weight: 400 as const, font: 'inter' }
  const half = key === 'signer1' || key === 'signer2'
  const w = half ? 30 : 70
  const x = key === 'signer1' ? 12 : key === 'signer2' ? 58 : 15

  return {
    key,
    box: fitBox({ x, y: preset.y, w, h: preset.h }, area),
    font: preset.font,
    weight: preset.weight,
    size: preset.size,
    minSize: preset.minSize,
    color: '#1f2937',
    align: 'center',
    valign: 'middle',
    uppercase: false
  }
}

/** What an art with no boxes starts with: the three the API requires. */
export const seedLayout = (template: Pick<CertificateTemplate, 'backgroundWidth' | 'backgroundHeight' | 'visibleArea'>): LayoutField[] =>
  REQUIRED_FIELDS.map((key) => defaultField(key, template))

/** The community's primary shades, read off the CSS custom properties the theme sets. */
export const themeSwatches = (): string[] => {
  if (typeof window === 'undefined') return []
  const style = getComputedStyle(document.documentElement)
  return ['--color-primary-900', '--color-primary-700', '--color-primary-500', '--color-secondary-700']
    .map((name) => style.getPropertyValue(name).trim())
    .filter((value) => /^#[0-9a-f]{6}$/i.test(value))
}

/** Pulls the API's field messages out of an error, for a list under the form. */
export const apiErrorMessages = (err: unknown, fallback: string): string[] => {
  const e = err as { message?: string; response?: { data?: { message?: string; errors?: unknown } } } | null
  const data = e?.response?.data
  const list = Array.isArray(data?.errors)
    ? (data.errors as Array<{ message?: string } | null>).map((item) => item?.message).filter(Boolean)
    : []
  if (list.length) return Array.from(new Set(list as string[]))
  return [data?.message || e?.message || fallback]
}

/**
 * The management view of a certificates space: `/{section}/{space}/manage`
 * (`/gerenciar` before; old links are redirected by `routes.js`).
 * It rides on the item segment, which for this module is otherwise a
 * certificate id (a UUID), so the two never collide.
 */
export const MANAGE_SEGMENT = ROUTES.certificatesManageSegment

const BACK_KEY = 'memberfy:certificates:back'

/**
 * Where a certificate's "back" leads: the space list or the management view,
 * whichever opened it. Kept for the tab only; a direct link falls back to the
 * space.
 */
export const rememberCertificateOrigin = (path: string) => {
  try {
    sessionStorage.setItem(BACK_KEY, path)
  } catch {
    /* Private mode: back goes to the space. */
  }
}

export const certificateOrigin = (fallback: string): string => {
  try {
    const saved = sessionStorage.getItem(BACK_KEY)
    return saved && saved.startsWith(fallback) ? saved : fallback
  } catch {
    return fallback
  }
}
